import React from 'react';
import { FiSearch, FiX } from 'react-icons/fi';
import { C, YEARS, MONTHS } from './tokens';

/**
 * Shared filter bar used across all report tabs.
 *
 * Feature flags (per-tab):
 *   showUser        – User dropdown
 *   showDateWise    – specific date input (overrides month)
 *   showLocation    – location text input
 *   showChannel     – Channel dropdown
 *   showBsa         – BSA dropdown
 */
export default function ReportFilters({
  // Month + Year
  year, setYear, month, setMonth,
  // User
  selUser, setSelUser, users = [], showUser = false,
  // Date Wise
  dateFrom, setDateFrom, dateTo, setDateTo, showDateWise = false,
  // Location
  location, setLocation, showLocation = false,
  // Channel
  channelFilter, setChannelFilter, channels = [], showChannel = false,
  // BSA
  bsaFilter, setBsaFilter, bsas = [], showBsa = false,
  // Actions
  onLoad, loading = false, searched = false, onReset,
}) {
  const sel = {
    height: 36, border: `1px solid ${C.border}`, borderRadius: 8,
    padding: '0 10px', fontSize: 13, color: C.t1, background: C.white,
    outline: 'none', fontFamily: 'inherit',
  };

  return (
    <div style={{ background: C.white, borderRadius: 12, border: `1px solid ${C.border}`,
      padding: '16px 20px', boxShadow: '0 2px 6px rgba(13,59,94,0.06)' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: C.t4,
        textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>
        Filters
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>

        {/* Month + Year — always shown */}
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: C.t2, display: 'block', marginBottom: 5 }}>Month</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <select value={month} onChange={e => setMonth(e.target.value)} style={{ ...sel, minWidth: 130 }}>
              {MONTHS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
            <select value={year} onChange={e => setYear(e.target.value)} style={{ ...sel, minWidth: 80 }}>
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        {/* User Wise */}
        {showUser && (
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.t2, display: 'block', marginBottom: 5 }}>User Wise</label>
            <select value={selUser} onChange={e => setSelUser(e.target.value)} style={{ ...sel, minWidth: 170 }}>
              <option value="">All Users</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>
                  {[u.first_name, u.last_name].filter(Boolean).join(' ') || u.email}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Channel */}
        {showChannel && (
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.t2, display: 'block', marginBottom: 5 }}>Channel</label>
            <select value={channelFilter} onChange={e => setChannelFilter(e.target.value)} style={{ ...sel, minWidth: 170 }}>
              <option value="">All Channels</option>
              {channels.map(c => (
                <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
              ))}
            </select>
          </div>
        )}

        {/* BSA */}
        {showBsa && (
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.t2, display: 'block', marginBottom: 5 }}>BSA</label>
            <select value={bsaFilter} onChange={e => setBsaFilter(e.target.value)} style={{ ...sel, minWidth: 170 }}>
              <option value="">All BSAs</option>
              {bsas.map(b => (
                <option key={b.id} value={b.id}>{b.name} ({b.code})</option>
              ))}
            </select>
          </div>
        )}

        {/* Date Wise */}
        {showDateWise && (
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.t2, display: 'block', marginBottom: 5 }}>
              Date Range{' '}
              <span style={{ fontWeight: 400, color: C.t4, fontSize: 11 }}>(overrides month)</span>
            </label>
            <div style={{ display: 'flex', gap: 6 }}>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                style={{ ...sel, minWidth: 145 }}/>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                min={dateFrom || undefined}
                style={{ ...sel, minWidth: 145 }}/>
            </div>
          </div>
        )}

        {/* Location Wise */}
        {showLocation && (
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.t2, display: 'block', marginBottom: 5 }}>Location Wise</label>
            <input type="text" value={location} placeholder="City / Location…"
              onChange={e => setLocation(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && onLoad()}
              style={{ ...sel, minWidth: 140 }}/>
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 8, paddingBottom: 1 }}>
          <button onClick={onLoad} disabled={loading}
            style={{ height: 36, padding: '0 22px', background: loading ? C.navyM : C.navy,
              color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700,
              cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: 6, opacity: loading ? 0.75 : 1 }}>
            {loading
              ? <><div style={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.35)', borderTopColor: '#fff', animation: 'spin 0.8s linear infinite' }}/> Loading…</>
              : <><FiSearch size={13}/> Load Report</>}
          </button>
          {searched && onReset && (
            <button onClick={onReset}
              style={{ height: 36, padding: '0 14px', background: C.navyL, border: `1px solid ${C.border}`,
                borderRadius: 8, color: C.navy, cursor: 'pointer', fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 600 }}>
              <FiX size={13}/> Reset
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
